import { useEffect, useRef, useState } from 'react'
import { useModal } from '../../context/ModalContext'
import {
  acceptUser,
  connectUser,
  denyUser,
  getUser,
  removeConnection,
} from '../../services/users'
import { period, personName, relationLabel } from './profileFormat'

function ProfileActions({ profile, busy, onConnect, onAccept, onDeny, onRemove, onEdit }) {
  if (profile.relation === 'self') {
    return (
      <button type="button" className="net-btn" onClick={onEdit}>
        Edit profile
      </button>
    )
  }
  if (profile.relation === 'connected') {
    return (
      <button type="button" className="net-btn is-danger" onClick={onRemove} disabled={busy}>
        Remove connection
      </button>
    )
  }
  if (profile.relation === 'incoming') {
    return (
      <>
        <button type="button" className="net-btn is-primary" onClick={onAccept} disabled={busy}>
          Accept
        </button>
        <button type="button" className="net-btn" onClick={onDeny} disabled={busy}>
          Deny
        </button>
      </>
    )
  }
  if (profile.relation === 'outgoing') {
    return (
      <button type="button" className="net-btn" disabled>
        Requested
      </button>
    )
  }
  return (
    <button type="button" className="net-btn is-primary" onClick={onConnect} disabled={busy}>
      Connect
    </button>
  )
}

function History({ title, rows, primary, secondary }) {
  return (
    <section className="net-section">
      <h3>{title}</h3>
      {rows.length === 0 ? <p className="net-empty">None yet.</p> : null}
      {rows.map((entry, index) => (
        <div className="net-history" key={`${title}-${index}`}>
          <div>
            <div className="net-history-title">{primary(entry)}</div>
            {secondary(entry) ? <div className="net-history-sub">{secondary(entry)}</div> : null}
          </div>
          <div className="net-history-period">{period(entry)}</div>
        </div>
      ))}
    </section>
  )
}

function ProfileView({ profile, onOpenUser, onChanged }) {
  const { openModal } = useModal()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const run = async (action) => {
    setBusy(true)
    setError('')
    try {
      const next = await action()
      onChanged?.(next)
    } catch (err) {
      setError(err.message || 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  const count = profile.connectionCount || 0
  const meta = [profile.occupation, profile.company, profile.location].filter(Boolean)

  return (
    <div className="net-profile">
      <header className="net-head">
        <div>
          <h2>{personName(profile)}</h2>
          {meta.length ? <p className="net-meta">{meta.join(' · ')}</p> : null}
          <p className="net-meta">
            {count} {count === 1 ? 'connection' : 'connections'}
            {relationLabel(profile.relation) && profile.relation !== 'self'
              ? ` · ${relationLabel(profile.relation)}`
              : ''}
          </p>
        </div>
        <div className="net-actions">
          <ProfileActions
            profile={profile}
            busy={busy}
            onConnect={() => run(() => connectUser(profile._id))}
            onAccept={() => run(() => acceptUser(profile._id))}
            onDeny={() => run(() => denyUser(profile._id))}
            onRemove={() =>
              openModal('confirm', {
                title: 'Remove connection',
                message: `Remove ${personName(profile)} from your connections?`,
                confirmLabel: 'Remove',
                danger: true,
                onConfirm: () => removeConnection(profile._id),
                onConfirmed: () => onChanged?.(),
              })
            }
            onEdit={() =>
              openModal('profile-edit', {
                profile,
                onSaved: () => onChanged?.(),
              })
            }
          />
        </div>
      </header>

      {error ? <p className="net-error">{error}</p> : null}
      {profile.bio ? <p className="net-bio">{profile.bio}</p> : null}

      <History
        title="Experience"
        rows={profile.experience || []}
        primary={(entry) => entry.title || entry.company}
        secondary={(entry) => (entry.title ? entry.company : '')}
      />
      <History
        title="Education"
        rows={profile.education || []}
        primary={(entry) => entry.school}
        secondary={(entry) => entry.major}
      />

      <section className="net-section">
        <h3>Connections</h3>
        {(profile.connections || []).length === 0 ? <p className="net-empty">None yet.</p> : null}
        {(profile.connections || []).map((person) => (
          <button
            key={person._id}
            type="button"
            className="net-connection"
            onClick={() => onOpenUser?.(person)}
          >
            <span>{personName(person)}</span>
            <span className="net-row-meta">
              {[person.occupation, relationLabel(person.relation)].filter(Boolean).join(' · ')}
            </span>
          </button>
        ))}
      </section>
    </div>
  )
}

// One grid window: the person `userId` points at.
export function NetworkWindowBody({ userId, revision, onOpenUser, onChanged, onProfile }) {
  const [profile, setProfile] = useState(null)
  const [status, setStatus] = useState(userId ? 'loading' : 'idle')
  const onProfileRef = useRef(onProfile)
  onProfileRef.current = onProfile

  useEffect(() => {
    setProfile(null)
  }, [userId])

  useEffect(() => {
    if (!userId) {
      setStatus('idle')
      return undefined
    }
    let alive = true
    setStatus('loading')
    getUser(userId)
      .then((user) => {
        if (!alive) return
        setProfile(user)
        setStatus('ready')
        onProfileRef.current?.(user)
      })
      .catch(() => {
        if (alive) setStatus('error')
      })
    return () => {
      alive = false
    }
  }, [userId, revision])

  if (!userId) return <div className="display-empty">Select a person from the list.</div>
  if (status === 'loading' && !profile) return <div className="display-empty">Loading…</div>
  if (status === 'error' || !profile) return <div className="display-empty">Could not load this profile.</div>

  return (
    <div className="net-window">
      <ProfileView
        profile={profile}
        onOpenUser={onOpenUser}
        onChanged={(next) => {
          if (next?._id) {
            setProfile(next)
            onProfile?.(next)
          }
          onChanged?.(next)
        }}
      />
    </div>
  )
}

export default NetworkWindowBody
